import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { assignUsersToReport, getReports} from '@/services/report-service';
import { useToast } from '@/hooks/use-toast';
import { getClientUsers } from '@/services/tenant-service';
import { ClientUser } from '@/types/user';
import { Report, UserReportAssignmentRequest } from '@/types/report';
import { useUser } from "@/hooks/useUser";

export function ReportUserAssignment() {
  const [selectedReportId, setSelectedReportId] = useState<number | null>(null);
  const [selectedUsers, setSelectedUsers] = useState<{ [key: string]: boolean }>({});
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const {userInfo} = useUser();

  const { data: reports, isLoading: isLoadingReports } = useQuery<Report[]>({
    queryKey: ['reports'],
    queryFn: getReports,
  });

  const { data: users, isLoading: isLoadingUsers } = useQuery<ClientUser[]>({
    queryKey: ['users', userInfo?.organisation],
    queryFn: () => getClientUsers(userInfo?.organisation ?? ''),
  });

  const assignMutation = useMutation({
    mutationFn: assignUsersToReport,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reportUsers', selectedReportId] });
      toast({
        title: 'Success',
        description: 'Users assigned to report successfully',
      });
      resetForm()
    },
    onError: () => {
      toast({
        title: 'Error',
        description: 'Failed to assign users to report',
        variant: 'destructive',
      });
    },
  });

const resetForm = () => {
    setSelectedReportId(null);
    setSelectedUsers({});
  };

  const handleReportChange = (reportId: string) => {
    setSelectedReportId(Number(reportId));
    setSelectedUsers({});
  };

  const handleUserToggle = (userId: string) => {
    setSelectedUsers(prev => ({ ...prev, [userId]: !prev[userId] }));
  };

  const handleAssign = () => {
    if (!selectedReportId || !users) return;

    const assignments: UserReportAssignmentRequest[] = Object.entries(selectedUsers)
      .filter(([_, isSelected]) => isSelected)
      .map(([authorisationIdStr]) => {
        const authorisationId = authorisationIdStr;
        const user = users.find(u => u.authorisationId === authorisationId);
        if (!user) {
          throw new Error(`User with ID ${authorisationId} not found`);
        }
        return {
          reportId: selectedReportId,
        //   userId: userId,
          userName: user.authorisationId,
          authId: user.authorisationId,
          permissions: ['CanView'],
        };
      });

    assignMutation.mutate(assignments);
  };

  if (isLoadingReports || isLoadingUsers) {
    return <div>Loading...</div>;
  }

  return (
    <Card className="w-full max-w-2xl mx-auto mt-8">
      <CardHeader>
        <CardTitle className="text-2xl font-bold text-center">Assign Users to Report</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-2">
          <Label htmlFor="report">Select Report</Label>
          <Select key={selectedReportId} onValueChange={handleReportChange} value={selectedReportId?.toString()}>
            <SelectTrigger id="report">
              <SelectValue placeholder="Select a report" />
            </SelectTrigger>
            <SelectContent>
              {reports?.map((report) => (
                <SelectItem key={report.id} value={report.id.toString()}>
                  {report.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {selectedReportId && (
          <div className="space-y-2">
            <Label>Select Users</Label>
            <div className="space-y-2">
              {users?.map((user) => (
                <div key={user.authorisationId} className="flex items-center space-x-2">
                  <Checkbox
                    id={`user-${user.authorisationId}`}
                    checked={selectedUsers[user.authorisationId] || false}
                    onCheckedChange={() => handleUserToggle(user.authorisationId)}
                  />
                  <Label htmlFor={`user-${user.authorisationId}`}>{user.userName}</Label>
                </div>
              ))}
            </div>
          </div>
        )}
        <Button
          onClick={handleAssign}
          disabled={!selectedReportId || Object.values(selectedUsers).filter(Boolean).length === 0}
          className="w-full"
        >
          Assign Users to Report
        </Button>
      </CardContent>
    </Card>
  );
}